import { UserCheck } from "lucide-react";
import type { ConsultaDaSala, EstadoDaSala } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { CLASSES_DO_TOM, descrever, quem } from "./estado-na-tela";
import type { SalaAgora } from "./use-salas-agora";

type Props = {
  salas: SalaAgora[];
  porSala: Record<string, EstadoDaSala>;
  semSala: ConsultaDaSala[];
  /** Abre o detalhe da consulta — só chega quando quem vê pode saber quem é o paciente. */
  onAbrir?: (consultaId: string) => void;
};

/** Um cartão por sala: o estado agora, quem está (ou vem a seguir), o dentista e o horário. */
export function ListaAgora({ salas, porSala, semSala, onAbrir }: Props) {
  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {salas.map((s) => (
          <CartaoDaSala key={s.id} sala={s} estado={porSala[s.id]!} onAbrir={onAbrir} />
        ))}
      </div>
      {semSala.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">Em atendimento sem sala</h3>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {semSala.map((c) => (
              <CartaoDaSala key={c.id} estado={{ tipo: "em-atendimento", consulta: c, desde: null, passouDoHorario: false }} onAbrir={onAbrir} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function CartaoDaSala({ sala, estado, onAbrir }: { sala?: SalaAgora; estado: EstadoDaSala; onAbrir?: (id: string) => void }) {
  const d = descrever(estado);
  const abrivel = !!d.consulta?.pacienteNome && !!onAbrir;
  return (
    <button
      type="button"
      disabled={!abrivel}
      onClick={() => d.consulta && onAbrir?.(d.consulta.id)}
      className={cn(
        "flex flex-col gap-2 rounded-xl border p-4 text-left shadow-sm transition-colors",
        CLASSES_DO_TOM[d.tom].caixa,
        abrivel ? "hover:border-zinc-300 dark:hover:border-border" : "cursor-default",
      )}
    >
      <span className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: sala?.cor ?? "#a1a1aa" }} />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-zinc-900 dark:text-foreground">{sala?.nome ?? "Sem sala"}</span>
        <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide", CLASSES_DO_TOM[d.tom].selo)}>{d.rotulo}</span>
      </span>
      {d.consulta ? (
        <span className="space-y-0.5">
          <span className="block truncate text-[15px] font-medium text-zinc-900 dark:text-foreground">{quem(d.consulta)}</span>
          <span className="block truncate text-xs text-zinc-500 dark:text-muted-foreground">
            {[d.dentista, d.horario].filter(Boolean).join(" · ")}
          </span>
        </span>
      ) : (
        <span className="text-xs text-zinc-500 dark:text-muted-foreground">{d.horario}</span>
      )}
      {d.chegou && (
        <span className="inline-flex w-fit items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
          <UserCheck className="h-3 w-3" />
          Paciente chegou
        </span>
      )}
    </button>
  );
}
